import { useState } from 'react';
import { format, addDays, differenceInDays, parseISO } from 'date-fns';
import { Sprout, RotateCcw, Trash2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getGerminationDays } from '@/lib/germinationDays';
import { rescheduleAfterGermination } from '@/lib/growthAnchoring';

interface GerminationCheckDialogProps {
  task: {
    id: string;
    task_type: string;
    title: string;
    description: string | null;
    scheduled_date: string;
    estimated_minutes: number | null;
    completed: boolean | null;
    user_plant_id: string;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskUpdated: () => void;
  userId: string;
}

export function GerminationCheckDialog({
  task, open, onOpenChange, onTaskUpdated, userId,
}: GerminationCheckDialogProps) {
  const [processing, setProcessing] = useState(false);

  if (!task) return null;

  // Extract plant name from title: "Carrots: Check Germination" → "Carrots"
  const plantName = task.title.split(':')[0]?.trim() || '';

  const handleGerminated = async () => {
    setProcessing(true);
    try {
      const today = new Date();
      await supabase.from('garden_tasks').update({
        completed: true,
        completed_at: today.toISOString(),
      }).eq('id', task.id);

      // Re-anchor downstream growth stages (harden_off, plant_out, transplants)
      // to the actual germination date — not the seasonal calendar.
      // Fetch the user's zone for frost-floor logic.
      const { data: profile } = await supabase
        .from('profiles')
        .select('growing_zone')
        .eq('user_id', userId)
        .maybeSingle();
      const zone = profile?.growing_zone || 'UK-Midlands';

      const updated = await rescheduleAfterGermination({
        userPlantId: task.user_plant_id,
        germinationDate: today,
        plantName,
        zone,
      });

      if (updated > 0) {
        toast.success(`${plantName} germinated! 🌱 Next ${updated} task${updated > 1 ? 's' : ''} re-timed to its actual growth.`);
      } else {
        toast.success(`${plantName} germinated! 🌱 Your timeline continues.`);
      }
      onTaskUpdated();
      onOpenChange(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleRetry = async () => {
    setProcessing(true);
    try {
      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');

      // Mark this germination check as completed (outcome: retry)
      await supabase.from('garden_tasks').update({
        completed: true,
        completed_at: new Date().toISOString(),
        description: (task.description || '') + ' (Result: retrying)',
      } as any).eq('id', task.id);

      // Get the original sowing task to know the sowing method
      const { data: sowTasks } = await supabase.from('garden_tasks')
        .select('*')
        .eq('user_plant_id', task.user_plant_id)
        .eq('task_type', 'seed_sowing')
        .order('scheduled_date', { ascending: false })
        .limit(1);

      const originalSow = sowTasks?.[0];
      const sowTitle = originalSow?.title || `${plantName}: Sow Seeds`;

      // Create new sowing task for today
      await supabase.from('garden_tasks').insert({
        user_id: userId,
        user_plant_id: task.user_plant_id,
        task_type: 'seed_sowing',
        title: sowTitle,
        description: `Re-sow — previous attempt did not germinate.`,
        scheduled_date: todayStr,
        estimated_minutes: originalSow?.estimated_minutes || 15,
      });

      // Shift all future uncompleted tasks for this plant
      // Calculate date shift: from original sow date to today
      const originalSowDate = originalSow ? parseISO(originalSow.scheduled_date) : today;
      const dayShift = differenceInDays(today, originalSowDate);

      if (dayShift > 0) {
        const { data: futureTasks } = await supabase.from('garden_tasks')
          .select('id, scheduled_date')
          .eq('user_plant_id', task.user_plant_id)
          .eq('completed', false)
          .neq('id', task.id)
          .neq('task_type', 'seed_sowing');

        if (futureTasks && futureTasks.length > 0) {
          for (const ft of futureTasks) {
            const oldDate = parseISO(ft.scheduled_date);
            const newDate = addDays(oldDate, dayShift);
            const newDateStr = format(newDate, 'yyyy-MM-dd');
            await supabase.from('garden_tasks')
              .update({ scheduled_date: newDateStr } as any)
              .eq('id', ft.id);
          }
        }
      }

      // Create new germination check
      const germDays = getGerminationDays(plantName);
      const checkDate = addDays(today, germDays);
      await supabase.from('garden_tasks').insert({
        user_id: userId,
        user_plant_id: task.user_plant_id,
        task_type: 'germination_check',
        title: `${plantName}: Check Germination`,
        description: `Check if ${plantName} seeds have sprouted (expected ~${germDays} days). If not, you can retry or remove the plant.`,
        scheduled_date: format(checkDate, 'yyyy-MM-dd'),
        estimated_minutes: 5,
      });

      toast.success(`New ${plantName} sowing added for today — check again in ${germDays} days! 🔄`);
      onTaskUpdated();
      onOpenChange(false);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleGiveUp = async () => {
    setProcessing(true);
    try {
      // Mark this germination check as completed
      await supabase.from('garden_tasks').update({
        completed: true,
        completed_at: new Date().toISOString(),
        description: (task.description || '') + ' (Result: gave up)',
      } as any).eq('id', task.id);

      // Delete all remaining uncompleted tasks for this plant
      await supabase.from('garden_tasks')
        .delete()
        .eq('user_plant_id', task.user_plant_id)
        .eq('completed', false);

      toast.success(`${plantName} removed from your schedule. You can always re-add it later.`);
      onTaskUpdated();
      onOpenChange(false);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Sprout className="h-5 w-5 text-primary" />
            {task.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            It's time to check if your <strong>{plantName}</strong> seeds have germinated. 
            Have you seen any sprouts?
          </p>

          <div className="space-y-2">
            {/* Yes — germinated */}
            <Button
              onClick={handleGerminated}
              disabled={processing}
              className="w-full rounded-xl garden-gradient shadow-soft hover:shadow-medium transition-all h-12 text-sm font-semibold gap-2"
            >
              <Check className="h-4 w-4" />
              Yes, they germinated! 🌱
            </Button>

            {/* Retry */}
            <Button
              variant="outline"
              onClick={handleRetry}
              disabled={processing}
              className="w-full rounded-xl border-border/60 h-11 text-sm gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              No — try sowing again
            </Button>

            {/* Give up */}
            <Button
              variant="ghost"
              onClick={handleGiveUp}
              disabled={processing}
              className="w-full rounded-xl text-muted-foreground h-10 text-sm gap-2 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Give up on this plant
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground text-center">
            "Try again" will re-sow today and shift your timeline. "Give up" removes all remaining tasks.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
